import { parseValue } from '@theme/themeUtils';
import { tokens } from '@theme/tokens';
import React from 'react';
import { View, ViewStyle } from 'react-native';

export interface DividerProps {
  /** Color of the divider line */
  color?: string;
  /** Height of the divider (for horizontal dividers) */
  height?: string | number;
  /** Width of the divider (for vertical dividers) */
  width?: string | number;
  /** Orientation of the divider */
  orientation?: 'horizontal' | 'vertical';
  /** Additional styles */
  style?: ViewStyle;
  /** Test ID for testing */
  testID?: string;
}

/**
 * Divider component - creates a horizontal or vertical line separator
 *
 * @example
 * // Basic horizontal divider
 * <Divider />
 *
 * // Custom height and color
 * <Divider height="2px" color="#F4F4F4" />
 *
 * // Vertical divider
 * <Divider orientation="vertical" width="1px" />
 */
export const Divider: React.FC<DividerProps> = ({
  color = tokens.colors.neutral.mist,
  height = '1px',
  width,
  orientation = 'horizontal',
  style,
  testID,
  ...props
}) => {
  // Parse height and width values
  const parsedHeight = parseValue(height);
  const parsedWidth = parseValue(width);

  // Determine dimensions based on orientation
  const dividerStyle: ViewStyle = {
    backgroundColor: color,
    ...(orientation === 'horizontal'
      ? {
          height: parsedHeight,
          width: '100%',
        }
      : {
          width: parsedWidth || parsedHeight, // Use height as width if width not specified
          height: '100%',
        }),
    ...style,
  };

  return <View style={dividerStyle} testID={testID} {...props} />;
};

export default Divider;
